// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Button, Card, CardContent, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// api
import { usePutEnergyGroupStateMutation } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import NodeTimeRangeGraph from '@/features/service-manager/components/energyxplorer/deep-dive/NodeTimeRangeGraph';
const EnergyGroupManagementDrawer = ({
  open,
  onClose,
  energyData,
  energyGroup,
}) => {
  const { t } = useTranslation('service-management');
  const [putEnergyGroupStatusMutation] = usePutEnergyGroupStateMutation();

  const handleStatusChange = async (newStatus) => {
    if (!energyGroup) return;
    try {
      await putEnergyGroupStatusMutation({
        groupId: energyGroup.energyGroupId,
        energyGroupState: newStatus,
      }).unwrap();
      onClose(); // Close the drawer after successful update
    } catch (error) {
      console.error('Failed to update energy group status:', error);
    }
  };
  return (
    <InsetDrawer
      title={t('energyxplorer.energy_group_details')}
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: '100%',
          maxWidth: {
            lg: '60vw',
            sm: '80vw',
            xs: '90vw',
          },
        },
      }}
    >
      {energyGroup ? (
        <>
          <Grid container spacing={2}>
            <Grid
              size={{
                lg: 12
              }}>
              <Card>
                <CardContent>
                  <HeaderDisplay label={t('energyxplorer.energy_group')} />
                  <MultiValueDisplay
                    label={t('common.name')}
                    value={energyGroup.name}
                  />
                  <MultiValueDisplay
                    label={t('common.id')}
                    value={energyGroup.energyGroupId}
                  />
                  <MultiValueDisplay
                    label={t('common.status')}
                    value={energyGroup?.options?.state}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleStatusChange('Desynced')}
                  >
                    {t('energyxplorer.desync_energy_group')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                lg: 12
              }}>
              <Card>
                <CardContent>
                  <HeaderDisplay label={t('energyxplorer.nodes')} />
                  <NodeTimeRangeGraph energyData={energyData} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>{' '}
        </>
      ) : (
        <Typography variant="body1">{t('common.no_account_data')}</Typography>
      )}
    </InsetDrawer>
  );
};

export default EnergyGroupManagementDrawer;

const HeaderDisplay = ({ label, description }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        mb: 2,
      }}
    >
      <Typography variant="body1" color="textSecondary" fontWeight="bold">
        {label}
      </Typography>
      {description && <InfoTooltipIcon title={description} />}
    </Box>
  );
};

const MultiValueDisplay = ({ label, value }) => {
  return (
    <Box>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="h6" color="textPrimary">
        {value}
      </Typography>
    </Box>
  );
};
